/**
 * Team API Service - RTK Query
 * BuilderBel Construction Project Management System
 *
 * Provides RTK Query endpoints for team management, invitations, and member operations.
 */

import { baseApi } from './api';
import type { ApiResponse, ProjectMember, TeamMemberInvitation } from '../types';

// Team member query options
interface TeamQueryOptions {
  projectId: string;
  page?: number;
  limit?: number;
  search?: string;
  role?: string;
  status?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  includeInactive?: boolean;
}

// Send invitations request
interface SendInvitationsRequest {
  projectId: string;
  emails: string[];
  role: string;
  permissions: string[];
  message?: string;
  emailStatuses?: Record<
    string,
    {
      exists: boolean;
      userId?: string;
      userData?: {
        firstName?: string;
        lastName?: string;
      };
    }
  >;
}

// Send invitations response
interface SendInvitationsResponse {
  success: {
    email: string;
    invitationId: string;
  }[];
  failed: {
    email: string;
    error: string;
  }[];
  totalSent: number;
  totalFailed: number;
}

// Update team member request
interface UpdateTeamMemberRequest {
  projectId: string;
  memberId: string;
  role?: string;
  permissions?: string[];
  status?: string;
}

/**
 * Team API Endpoints
 */
export const teamApi = baseApi.injectEndpoints({
  endpoints: builder => ({
    // Get project team members
    getProjectTeam: builder.query<
      ApiResponse<{
        data: ProjectMember[];
        total: number;
        page: number;
        limit: number;
      }>,
      TeamQueryOptions
    >({
      query: ({ projectId, ...params }) => ({
        url: `/projects/${projectId}/team`,
        params,
      }),
      providesTags: (result, _error, { projectId }) =>
        result
          ? [
              { type: 'Team' as const, id: projectId },
              { type: 'Team' as const, id: 'LIST' },
            ]
          : [{ type: 'Team' as const, id: 'LIST' }],
    }),

    // Get current user's project member record
    getCurrentProjectMember: builder.query<ApiResponse<ProjectMember>, { projectId: string }>({
      query: ({ projectId }) => ({
        url: `/projects/${projectId}/team/me`,
      }),
      providesTags: (_result, _error, { projectId }) => [
        { type: 'TeamMember' as const, id: projectId },
      ],
    }),

    // Send invitations to users
    sendInvitations: builder.mutation<ApiResponse<SendInvitationsResponse>, SendInvitationsRequest>(
      {
        query: data => ({
          url: '/invitations/send',
          method: 'POST',
          body: data,
        }),
        invalidatesTags: (_result, _error, { projectId }) => [
          { type: 'Team' as const, id: projectId },
          { type: 'Team' as const, id: 'LIST' },
          { type: 'Invitations' as const, id: projectId },
        ],
      }
    ),

    // Update team member
    updateTeamMember: builder.mutation<ApiResponse<ProjectMember>, UpdateTeamMemberRequest>({
      query: ({ projectId, memberId, ...data }) => ({
        url: `/projects/${projectId}/team/${memberId}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (_result, _error, { projectId }) => [
        { type: 'Team' as const, id: projectId },
        { type: 'Team' as const, id: 'LIST' },
        { type: 'TeamMember' as const, id: projectId },
      ],
    }),

    // Remove team member
    removeTeamMember: builder.mutation<ApiResponse<void>, { projectId: string; memberId: string }>({
      query: ({ projectId, memberId }) => ({
        url: `/projects/${projectId}/team/${memberId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { projectId }) => [
        { type: 'Team' as const, id: projectId },
        { type: 'Team' as const, id: 'LIST' },
      ],
    }),

    // Get project invitations
    getProjectInvitations: builder.query<
      ApiResponse<TeamMemberInvitation[]>,
      { projectId: string }
    >({
      query: ({ projectId }) => ({
        url: `/invitations/project/${projectId}`,
      }),
      providesTags: (_result, _error, { projectId }) => [
        { type: 'Invitations' as const, id: projectId },
        { type: 'Invitations' as const, id: 'LIST' },
      ],
    }),

    // Get current user's pending invitations
    getUserInvitations: builder.query<ApiResponse<TeamMemberInvitation[]>, void>({
      query: () => ({
        url: '/invitations/user',
      }),
      providesTags: [{ type: 'UserInvitations' as const, id: 'LIST' }],
    }),

    // Resend invitation
    resendInvitation: builder.mutation<ApiResponse<void>, { invitationId: string }>({
      query: ({ invitationId }) => ({
        url: `/invitations/${invitationId}/resend`,
        method: 'POST',
      }),
      invalidatesTags: [{ type: 'Invitations' as const, id: 'LIST' }],
    }),

    // Cancel invitation
    cancelInvitation: builder.mutation<
      ApiResponse<void>,
      { invitationId: string; projectId?: string }
    >({
      query: ({ invitationId }) => ({
        url: `/invitations/${invitationId}/cancel`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { projectId }) =>
        projectId
          ? [
              { type: 'Invitations' as const, id: projectId },
              { type: 'Invitations' as const, id: 'LIST' },
            ]
          : [{ type: 'Invitations' as const, id: 'LIST' }],
    }),

    // Respond to invitation (accept/decline)
    respondToInvitation: builder.mutation<
      ApiResponse<void>,
      { invitationId: string; action: 'accept' | 'decline'; message?: string }
    >({
      query: ({ invitationId, ...body }) => ({
        url: `/invitations/${invitationId}/respond`,
        method: 'POST',
        body,
      }),
      invalidatesTags: [
        { type: 'UserInvitations' as const, id: 'LIST' },
        { type: 'Team' as const, id: 'LIST' },
      ],
    }),

    // Get invitation by token (for accepting invitations)
    getInvitationByToken: builder.query<
      ApiResponse<TeamMemberInvitation & { projectName: string; invitedByName: string }>,
      { token: string }
    >({
      query: ({ token }) => ({
        url: `/invitations/token/${token}`,
      }),
    }),

    // Complete onboarding for new users
    completeOnboarding: builder.mutation<
      ApiResponse<{ id: string; email: string; firstName: string; lastName: string }>,
      {
        invitationToken: string;
        userData: {
          firstName: string;
          lastName: string;
          password: string;
          phone?: string;
          email: string;
        };
      }
    >({
      query: body => ({
        url: '/invitations/onboard',
        method: 'POST',
        body,
      }),
    }),
  }),
});

/**
 * Export hooks for use in components
 */
export const {
  useGetProjectTeamQuery,
  useGetCurrentProjectMemberQuery,
  useSendInvitationsMutation,
  useUpdateTeamMemberMutation,
  useRemoveTeamMemberMutation,
  useGetProjectInvitationsQuery,
  useGetUserInvitationsQuery,
  useResendInvitationMutation,
  useCancelInvitationMutation,
  useRespondToInvitationMutation,
  useGetInvitationByTokenQuery,
  useCompleteOnboardingMutation,
} = teamApi;
